/**
 * CricketAPIService — Fetches real match data for cricket, football and tennis.
 *
 * Source: /api/get-cached-sport.php?sport=<sport>, which serves the per-sport cache the
 * WebSocket sidecar rewrites every ~10s (backend/cache/<sport>.json).
 *
 * It previously tried two dead paths and so always returned zero matches:
 *   1. wsService.getCache('allmatches') — nothing ever subscribes to that topic, so the cache
 *      is permanently empty (the WS server only broadcasts subscribed topics).
 *   2. GET /api/get-cached-cricket.php — 404; that file has never existed on this server.
 * Despite the name this service is sport-agnostic; only the cricket-specific league filter is
 * gated to cricket (see _parseResponse).
 */

import { wsService } from './WebSocketService';
import { isLeagueBlocked } from '../config/blockedLeagues';

const API_BASE = '';
const SPORTS = ['cricket', 'football', 'tennis'];
const normaliseSport = (s) => (SPORTS.includes(String(s || '').toLowerCase()) ? String(s).toLowerCase() : 'cricket');
const cachedEndpoint = (sport) => `${API_BASE}/api/get-cached-sport.php?sport=${encodeURIComponent(sport)}`;

export const isMajorTournament = (competition) => {
  const name = (competition || '').toLowerCase();
  if (!name) return true; // empty competition = treat as major
  const minor = [
    'prime minister cup', 't20 meerut', 'european cricket',
    'provincial', 'sheffield shield', 'plunket shield',
    'nepal', 'tanzania', 'eswatini', 'ghana', 'seychelles', 'malawi',
    'saint helena', 'nigeria', 'rwanda', 'sub regional',
    'csa provincial', 'qualifier',
  ];
  return !minor.some(m => name.includes(m));
};

const CricketAPIService = {
  /**
   * Get all cricket matches — uses server-side cache for fast loads.
   * Falls back to direct proxy on error.
   */
  getAllMatches: async (sport = 'cricket') => {
    const s = normaliseSport(sport);
    try {
      const res = await fetch(cachedEndpoint(s));
      if (res.ok) {
        const data = await res.json();
        // An empty cache is a legitimate "no matches right now" — parse it so the UI shows its
        // empty state. Only a network/HTTP failure is treated as an error below.
        if (data && Array.isArray(data.result || data?.data?.result)) {
          return CricketAPIService._parseResponse(data, s);
        }
      }
    } catch (err) {
      console.error('CricketAPI getAllMatches error:', err);
    }
    // Reached only on a network/HTTP failure. Kept as success:false on purpose: callers such as
    // DashboardEventsSection only setState when success is true, so a transient blip leaves the
    // last good list on screen instead of blanking it.
    return { success: false, error: 'No data available', matches: [] };
  },

  /**
   * Parse a raw feed response ({result:[...]}) into a structured matches array.
   * @param {object} data  raw feed payload
   * @param {string} sport cricket | football | tennis — tags each match and decides whether the
   *                       cricket league blocklist applies.
   */
  _parseResponse: (data, sport = 'cricket') => {
    const s = normaliseSport(sport);
    if (data.error) return { success: false, error: data.error, matches: [] };
    const result = data?.data?.result || data?.result || [];
    const matchMap = new Map();

    // Filter out SRL (Simulated Reality League) matches — cannot be settled
    const isSRL = (m) => {
      const name = (m.event?.name || m.name || '').toUpperCase();
      return name.includes('SRL') || name.includes('SIMULATED REALITY');
    };

    // First pass: collect MATCH_ODDS markets (preferred), skip SRL
    result.forEach(m => {
      const gid = m.groupById || m.event?.id;
      if (!gid || isSRL(m)) return;
      if (m.mtype === 'MATCH_ODDS' && !matchMap.has(gid)) {
        matchMap.set(gid, m);
      }
    });
    // Second pass: add any match that has NO MATCH_ODDS but has other markets, skip SRL
    result.forEach(m => {
      const gid = m.groupById || m.event?.id;
      if (!gid || matchMap.has(gid) || isSRL(m)) return;
      if (m.mtype === 'MATCH_ODDS_SB' || m.mtype === 'BOOKMAKER' || m.runners?.length >= 2) {
        matchMap.set(gid, m);
      }
    });
    // Third pass: if any match is inPlay in ANY market, update the flag
    result.forEach(m => {
      const gid = m.groupById || m.event?.id;
      if (!gid || !matchMap.has(gid)) return;
      if (m.inPlay === true) {
        const existing = matchMap.get(gid);
        existing.inPlay = true; // Mark as live if ANY market says inPlay
      }
    });
    // Convert to structured format
    const matches = [];
    matchMap.forEach((m, gid) => {
      const ev = m.event || {};
      const comp = m.competition || {};
      const runners = m.runners || [];
      matches.push({
        id: gid, match_id: gid, groupById: gid,
        name: ev.name || m.name || '',
        home_team: runners[0]?.name || 'Team A',
        away_team: runners[1]?.name || 'Team B',
        draw_team: runners[2]?.name || null,
        league: comp.name || '', competition: comp.name || '',
        start_time: ev.openDate || '', start_time_ist: formatToIST(ev.openDate),
        inPlay: m.inPlay || false, status: m.status || 'OPEN',
        // lowercase alias: MatchCard (:328) and SportPageLayout's live-first sort both read
        // `inplay`, not `inPlay`, so without this no match ever shows as live on a sport page.
        inplay: m.inPlay || false,
        isBettable: m.isBettable || false, bettableTime: m.bettableTime || null,
        matched: m.matched || 0, sport: s,
        odds: {
          home: buildOddsFromRunner(runners[0]),
          away: buildOddsFromRunner(runners[1]),
          draw: runners[2] ? buildOddsFromRunner(runners[2]) : null,
        },
        runners,
        isMajor: isMajorTournament(comp.name),
      });
    });
    // Filter out blocked leagues — CRICKET ONLY. BLOCKED_LEAGUES holds 59 cricket competition
    // terms and isLeagueBlocked does a substring match, so applying it to football/tennis would
    // at best be a no-op and at worst silently hide fixtures (e.g. 'st lucia' matching a football
    // tie). Non-cricket sports have no blocklist of their own yet.
    const filtered = s === 'cricket'
      ? matches.filter(m => !isLeagueBlocked(m.competition || m.league || m.name))
      : matches;
    filtered.sort((a, b) => {
      if (a.inPlay && !b.inPlay) return -1;
      if (!a.inPlay && b.inPlay) return 1;
      return new Date(a.start_time) - new Date(b.start_time);
    });
    return { success: true, matches: filtered, count: filtered.length };
  },

  /**
   * Get all markets/odds for a single match.
   * Returns array of markets (MATCH_ODDS, MATCH_ODDS_SB, INNINGS_RUNS, etc.)
   */
  getMatchOdds: async (matchId, sport = 'cricket') => {
    // Helper to parse raw result into structured markets
    const parseMarkets = (result) => {
      const markets = [];
      result.forEach(m => {
        const runners = (m.runners || []).map(r => ({
          id: r.id,
          name: r.name,
          status: r.status || 'ACTIVE',
          back: (r.back || []).map(b => ({ price: b.price, sizeRaw: b.size || 0, line: b.line })),
          lay: (r.lay || []).map(l => ({ price: l.price, sizeRaw: l.size || 0, line: l.line })),
        }));

        // Normalize: back[0]=worst(left), back[2]=best(right, closest to lay)
        // API gives back[0]=best(highest), so reverse it
        // Lay is already correct: lay[0]=best(lowest, closest to back)
        runners.forEach(r => {
          r.back = r.back.slice().reverse(); // now [worst...best]
          while (r.back.length < 3) r.back.unshift({ price: null, sizeRaw: 0, line: null }); // pad left with nulls
          while (r.lay.length < 3) r.lay.push({ price: null, sizeRaw: 0, line: null }); // pad right with nulls
          r.back = r.back.slice(0, 3); // [worst, mid, best]
          r.lay = r.lay.slice(0, 3);   // [best, mid, worst]
        });

        let displayName = m.name || m.mtype;
        let originalName = (m.name || '').toLowerCase();
        let altNameLower = (m.altName || '').toLowerCase();
        let maxBet = '25K';
        // Only rename to BOOKMAKER if it's a real bookmaker market (not premium/isBetRadar, not toss/tied)
        const isMiniBookmaker = originalName.includes('mini bookmaker') || altNameLower.includes('mini bookmaker');
        const isBookmakerMarket =
          (m.mtype === 'MATCH_ODDS_SB' || m.mtype === 'BOOKMAKER') && !m.isBetRadar
          && (
            originalName === 'match odds' ||
            originalName === 'bookmaker' ||
            originalName === 'bookmaker 0 commission' ||
            originalName.includes('mini bookmaker') ||
            altNameLower.includes('bookmaker')
          )
          && !originalName.includes('toss') && !originalName.includes('to win the');
        if (isBookmakerMarket) {
          displayName = isMiniBookmaker ? 'MINI BOOKMAKER (0 Commission)' : 'BOOKMAKER (0 Commission)';
        }
        if (m.mtype === 'INNINGS_RUNS') maxBet = '25K';

        markets.push({
          id: m.id,
          name: displayName,
          originalName: m.name || '',
          mtype: m.mtype,
          btype: m.btype,
          status: m.status,
          statuslable: m.statusLabel || '',
          maxBet,
          suspended: m.status === 'SUSPENDED',
          isBetRadar: m.isBetRadar || false,
          isBookmaker: m.isBookmaker || false,
          tabGroupName: m.tabGroupName || '',
          inPlay: m.inPlay || false,
          selections: runners.map(r => ({
            name: r.name,
            status: r.status,
            odds: { back: r.back, lay: r.lay },
          })),
        });
      });
      return markets;
    };

    try {
      // 1. Try WebSocket cache (instant)
      const wsCached = wsService.getCache(`match_${matchId}_odds`);
      if (wsCached?.markets?.length > 0) {
        return { success: true, markets: wsCached.markets };
      }

      // 2. Fallback: server-side file cache — filter by matchId.
      // (Was fetch(CACHED_ENDPOINT) -> /api/get-cached-cricket.php, which 404s, so this
      //  fallback never worked either. Now points at the real per-sport cache.)
      const cachedRes = await fetch(cachedEndpoint(normaliseSport(sport)));
      if (cachedRes.ok) {
        const cachedData = await cachedRes.json();
        const allResult = cachedData?.data?.result || cachedData?.result || [];
        const matchResult = allResult.filter(m =>
          String(m.groupById || m.event?.id) === String(matchId)
        );
        if (matchResult.length > 0) {
          return { success: true, markets: parseMarkets(matchResult) };
        }
      }
    } catch (err) {
      console.error('CricketAPI getMatchOdds error:', err);
    }
    return { success: false, error: 'No data available', markets: [] };
  },

  /**
   * Get scorecard HTML for a live match.
   */
  getScorecard: async (matchId) => {
    try {
      // Try WebSocket cache (instant)
      const wsCached = wsService.getCache(`match_${matchId}_scorecard`);
      if (wsCached) {
        const html = typeof wsCached === 'string' ? wsCached : wsCached?.html || '';
        if (html) return { success: true, html };
      }
    } catch (err) {
      console.error('CricketAPI getScorecard error:', err);
    }
    return { success: false, error: 'No scorecard available', html: '' };
  },
};

// ─── Helpers ───

function buildOddsFromRunner(runner) {
  if (!runner) return { back: padArr([]), lay: padArr([]) };
  const backs = (runner.back || []).map(b => ({ price: b.price, sizeRaw: b.size || 0 }));
  const lays = (runner.lay || []).map(l => ({ price: l.price, sizeRaw: l.size || 0 }));
  return { back: padArr(backs), lay: padArr(lays) };
}

function padArr(arr) {
  const result = [...arr];
  while (result.length < 3) result.push({ price: null, sizeRaw: 0 });
  return result.slice(0, 3);
}

function formatToIST(isoDate) {
  if (!isoDate) return '';
  try {
    const d = new Date(isoDate);
    return d.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return isoDate; }
}

export default CricketAPIService;
